import React, { useState } from "react";
import { Droplets, Loader2, Clock, CheckCircle2, CalendarCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { submitQuoteRequest } from "../lib/api";
import { CAMPAIGN_LABEL } from "../lib/promotion";

export function TaktvattBooking({ serviceLabel, priceText, details, triggerClassName, triggerLabel, dayLong, campaignDiscount = false }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", address: "", zip: "", city: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleBook = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.address) {
      toast({ title: "Fyll i alla obligatoriska fält", description: "Namn, e-post, telefon och adress krävs." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast({ title: "Ogiltig e-postadress", description: "Kontrollera att e-postadressen är korrekt." });
      return;
    }
    setLoading(true);
    try {
      await submitQuoteRequest({
        type: "quote",
        service: serviceLabel,
        promotion: campaignDiscount ? CAMPAIGN_LABEL : null,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        address: form.address,
        zip: form.zip || null,
        city: form.city || null,
        message: `BOKNING (betalning efter utfört arbete): ${serviceLabel}`
          + (details ? ` – ${details}` : "")
          + (priceText ? ` · Riktpris: ${priceText}` : "")
          + (campaignDiscount ? " · 20% kampanjrabatt inkluderad" : ""),
      });
      setDone(true);
    } catch {
      toast({ variant: "destructive", title: "Något gick fel", description: "Kunde inte skicka bokningen. Försök igen." });
    } finally {
      setLoading(false);
    }
  };

  const reset = (v) => { setOpen(v); if (!v) { setDone(false); } };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button data-testid="taktvatt-book-trigger" className={triggerClassName}>
          {triggerLabel || `Boka ${serviceLabel}`}
        </Button>
      </DialogTrigger>
      <DialogContent className="light-card bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="taktvatt-booking-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="taktvatt-booking-done">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="mt-5 text-xl font-extrabold text-slate-900">Bokning mottagen!</h3>
            <p className="mt-2 text-slate-600 text-sm">Tack {form.firstName}! Vi hör av oss inom en arbetsdag och bokar in en tid. Du betalar först när arbetet är klart.</p>
            <Button onClick={() => reset(false)} className="mt-6 rounded-full h-11 px-8 bg-[#1B45C4] hover:bg-[#12308f] text-white font-bold" data-testid="taktvatt-booking-close">Stäng</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1B45C4]/10">
                <Droplets className="w-6 h-6 text-[#1B45C4]" />
              </div>
              <DialogTitle className="text-slate-900 text-xl">Boka {serviceLabel}</DialogTitle>
              <DialogDescription className="text-slate-500">
                {priceText ? `Riktpris efter 20% kampanjrabatt: ${priceText} · ` : ""}fri besiktning · betala efter utfört arbete
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <Label htmlFor="tb-fn" className="text-slate-700 text-xs font-semibold">Förnamn *</Label>
                <Input id="tb-fn" data-testid="taktvatt-firstname" value={form.firstName} onChange={set("firstName")} className="mt-1" />
              </div>
              <div className="col-span-1">
                <Label htmlFor="tb-ln" className="text-slate-700 text-xs font-semibold">Efternamn *</Label>
                <Input id="tb-ln" data-testid="taktvatt-lastname" value={form.lastName} onChange={set("lastName")} className="mt-1" />
              </div>
              <div className="col-span-1">
                <Label htmlFor="tb-em" className="text-slate-700 text-xs font-semibold">E-post *</Label>
                <Input id="tb-em" type="email" data-testid="taktvatt-email" value={form.email} onChange={set("email")} className="mt-1" />
              </div>
              <div className="col-span-1">
                <Label htmlFor="tb-ph" className="text-slate-700 text-xs font-semibold">Telefon *</Label>
                <Input id="tb-ph" data-testid="taktvatt-phone" value={form.phone} onChange={set("phone")} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="tb-ad" className="text-slate-700 text-xs font-semibold">Adress *</Label>
                <Input id="tb-ad" data-testid="taktvatt-address" value={form.address} onChange={set("address")} className="mt-1" />
              </div>
              <div className="col-span-1">
                <Label htmlFor="tb-zip" className="text-slate-700 text-xs font-semibold">Postnr</Label>
                <Input id="tb-zip" data-testid="taktvatt-zip" value={form.zip} onChange={set("zip")} className="mt-1" />
              </div>
              <div className="col-span-1">
                <Label htmlFor="tb-city" className="text-slate-700 text-xs font-semibold">Ort</Label>
                <Input id="tb-city" data-testid="taktvatt-city" value={form.city} onChange={set("city")} className="mt-1" />
              </div>
            </div>

            <div className="flex items-start gap-2.5 rounded-xl bg-[#f0f7ff] p-3.5 text-sm text-slate-600">
              <Clock className="w-5 h-5 text-[#1B45C4] shrink-0 mt-0.5" />
              <span>{dayLong ? <>Arbetet tar oftast <strong>ca en dag</strong>. </> : null}Du betalar <strong>först när arbetet är klart</strong> – vi hör av oss och bokar in en tid som passar dig.</span>
            </div>

            <Button onClick={handleBook} disabled={loading} data-testid="taktvatt-book-submit"
              className="w-full rounded-full h-12 bg-[#1B45C4] hover:bg-[#12308f] text-white font-bold text-base">
              {loading ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Skickar…</> : <><CalendarCheck className="mr-2 w-5 h-5" /> Skicka bokning</>}
            </Button>
            <p className="text-center text-xs text-slate-400">Ingen betalning nu · fri besiktning ingår</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
